import React, { useState, useEffect } from "react";
import { alpha, Box, Typography, Divider, Stack } from "@mui/material";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DateRangeIcon from "@mui/icons-material/DateRange";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { formatDateWithYear } from "../../utils/hooks/dataHandler";
import { AIAssistantButton } from "../Schedules/CA/FaAddition/AIAssistant";
import { JustInfo } from "../utils/Icon";
import PinButton from "../utils/button/PinButton";
import {
  getUserPreference,
  updateGeneralPreference,
} from "../../contexts/services/userPreferenceServices";

const BaseHeader = ({
  title,
  companyName,
  uen,
  yearOfAssessment,
  financialYearStart,
  financialYearEnd,
  currency,
  actions,
  sticky = true,
  aiSettings = {
    canUseAi: false,
    renderAiType: "",
  },
  companyType = "common",
  showPrecedingDays = false,
  crossYAApportionmentContext,
  children,
}) => {
  const [isSticky, setIsSticky] = useState(() => {
    try {
      const saved = localStorage.getItem("baseHeader.stickyPin");
      if (saved !== null) return saved === "true";
    } catch {}
    return sticky;
  });

  useEffect(() => {
    try {
      localStorage.setItem("baseHeader.stickyPin", String(isSticky));
    } catch {}
  }, [isSticky]);

  useEffect(() => {
    let isMounted = true;

    const loadPreference = async () => {
      try {
        const response = await getUserPreference();
        const preference = response?.data || response;
        const stickyPin = preference?.general?.header?.stickyPin;

        if (!isMounted) return;
        if (typeof stickyPin === "boolean") {
          setIsSticky((prev) => (prev === stickyPin ? prev : stickyPin));
        }
      } catch {
        // Keep the localStorage value when remote preference loading fails.
      }
    };

    loadPreference();

    return () => {
      isMounted = false;
    };
  }, []);

  const saveStickyPreference = async (stickyPin) => {
    try {
      await updateGeneralPreference({
        value: {
          header: {
            stickyPin,
          },
        },
      });
    } catch {
      // Local storage remains the fallback for offline or unauthenticated states.
    }
  };

  const handleStickyToggle = () => {
    const nextSticky = !isSticky;
    setIsSticky(nextSticky);
    saveStickyPreference(nextSticky);
  };
  const isInvestmentCompany = companyType === "investment";
  const informationColumns = [
    "minmax(0, 1fr)",
    currency && "minmax(0, 0.65fr)",
    yearOfAssessment && "minmax(0, 1fr)",
    financialYearStart && financialYearEnd && "minmax(0, 1.65fr)",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Box
      sx={{
        position: isSticky ? "sticky" : "relative",
        top: isSticky ? 25 : 0,
        zIndex: isSticky ? 1000 : "auto",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Box
        sx={(theme) => ({
          width: "100%",
          boxSizing: "border-box",
          backgroundColor: isSticky
            ? alpha(theme.palette.background.paper, 0.96)
            : theme.palette.background.paper,

          backdropFilter: isSticky ? "blur(12px)" : "none",
          WebkitBackdropFilter: isSticky ? "blur(12px)" : "none",

          borderRadius: isSticky ? "16px" : "10px",
          border: "1px solid",
          borderColor: isSticky
            ? alpha(theme.palette.primary.main, 0.16)
            : theme.palette.divider,

          boxShadow: isSticky
            ? `0 8px 24px -8px ${alpha(theme.palette.common.black, 0.12)},
     0 2px 6px -2px ${alpha(theme.palette.common.black, 0.05)}`
            : "none",

          padding: "16px",

          transition:
            "background-color 220ms ease, box-shadow 220ms ease, border-color 220ms ease, border-radius 220ms ease",

          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
          },
        })}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Box sx={{ flex: "1 1 320px", minWidth: 0, maxWidth: "100%" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                columnGap: 1,
                rowGap: 0.5,
                flexWrap: "wrap",
                minWidth: 0,
              }}
            >
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                  fontSize: { xs: "1rem", sm: "1.125rem" },
                  lineHeight: 1.35,
                  minWidth: 0,
                  maxWidth: "100%",
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                }}
              >
                {companyName}
              </Typography>
              {isInvestmentCompany && (
                <Box
                  component='span'
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: "secondary.main",
                    color: "secondary.contrastText",
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    flexShrink: 0,
                  }}
                >
                  Investment Holding
                </Box>
              )}
            </Box>
            <Typography
              variant='body2'
              sx={{
                color: "text.secondary",
                fontSize: "0.8125rem",
                lineHeight: 1.35,
                overflowWrap: "anywhere",
              }}
            >
              {title}
            </Typography>
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              flexWrap: "wrap",
              flex: "0 1 auto",
              minWidth: 0,
              maxWidth: "100%",
              ml: "auto",
              gap: 1,
            }}
          >
            {aiSettings.canUseAi && aiSettings.renderAiType !== "" && (
              <AIAssistantButton type={aiSettings.renderAiType} />
            )}
            <PinButton pinned={isSticky} onClick={handleStickyToggle} />
            {actions}
            {children}
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: informationColumns,
            },
            alignItems: "start",
            width: "100%",
            columnGap: { xs: 2, md: 3 },
            rowGap: 1.5,
            "& > .MuiBox-root": { minWidth: 0, maxWidth: "100%" },
            "& .MuiTypography-body1": { lineHeight: 1.4 },
          }}
        >
          {/* Tax Reference Number */}
          <Box>
            <Box display='flex' alignItems='flex-start' gap={1}>
              <BookmarkIcon
                sx={{ color: "#424242", fontSize: 20, flexShrink: 0, mt: 0.5 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant='caption'
                  sx={{ color: "#9E9E9E", fontWeight: 600 }}
                >
                  Tax Reference Number
                </Typography>
                <Typography
                  variant='body1'
                  sx={{
                    fontWeight: 500,
                    color: "#424242",
                    overflowWrap: "anywhere",
                  }}
                >
                  {uen}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Currency */}
          {currency && (
            <Box>
              <Box display='flex' alignItems='flex-start' gap={1}>
                <AttachMoneyIcon
                  sx={{
                    color: "#424242",
                    fontSize: 20,
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant='caption'
                    sx={{ color: "#9E9E9E", fontWeight: 600 }}
                  >
                    Currency
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{
                      fontWeight: 500,
                      color: "#424242",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {currency}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Year of Assessment */}
          {yearOfAssessment && (
            <Box>
              <Box display='flex' alignItems='flex-start' gap={1}>
                <CalendarTodayIcon
                  sx={{
                    color: "#424242",
                    fontSize: 20,
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant='caption'
                    sx={{ color: "#9E9E9E", fontWeight: 600 }}
                  >
                    Year of Assessment
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ fontWeight: 500, color: "#424242" }}
                  >
                    {yearOfAssessment}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Basis Period */}
          {financialYearStart && financialYearEnd && (
            <Box sx={{ gridColumn: { xs: "1 / -1", lg: "auto" } }}>
              <Box
                display='flex'
                alignItems='flex-start'
                gap={1}
                sx={{
                  minWidth: 0,
                  justifyContent: { xs: "flex-start", lg: "flex-end" },
                }}
              >
                <DateRangeIcon
                  sx={{
                    color: "#424242",
                    fontSize: 20,
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction='row' alignItems='center' spacing={1}>
                    <Typography
                      variant='caption'
                      sx={{ color: "#9E9E9E", fontWeight: 600 }}
                    >
                      Basis Period
                    </Typography>
                    {showPrecedingDays && (
                      <JustInfo
                        title={
                          <>
                            <span>
                              Preceding YA Days:{" "}
                              {crossYAApportionmentContext?.precedingDays}
                            </span>
                            <br />
                            <span>
                              Current YA Days:{" "}
                              {crossYAApportionmentContext?.totalDays -
                                crossYAApportionmentContext?.precedingDays}
                            </span>
                            <br />
                            <span>
                              Total Days:{" "}
                              {crossYAApportionmentContext?.totalDays}
                            </span>
                          </>
                        }
                      />
                    )}
                  </Stack>
                  <Typography
                    variant='body1'
                    component='div'
                    sx={{
                      fontWeight: 500,
                      color: "#424242",
                      display: "flex",
                      flexWrap: "wrap",
                      columnGap: 0.5,
                      rowGap: 0.25,
                    }}
                  >
                    <Box component='span' sx={{ whiteSpace: "nowrap" }}>
                      {formatDateWithYear(financialYearStart)}
                    </Box>
                    <Box component='span' sx={{ whiteSpace: "nowrap" }}>
                      {"– "}
                      {formatDateWithYear(financialYearEnd)}
                    </Box>
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default BaseHeader;
